import {
  STORE_ORDER_SUMMARY_EST_DELIVERY_MAX_DAYS_AFTER_CLOSE,
  STORE_ORDER_SUMMARY_EST_DELIVERY_MIN_DAYS_AFTER_CLOSE,
} from '../constants/storeCheckout.constants';
import { resolveStoreReviewProductTitle } from './storeCheckoutProductTitleUpdate.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import {
  formatAbbreviatedMonthDay,
  formatAbbreviatedMonthDayRange,
  parseLocalDateOnly,
} from '@/utilities/DateUtils';

export interface IStoreOrderSummaryProductGroup {
  primaryItem: ICartItemDetail;
  colors: Array<{ hex: string; color: string }>;
}

/** Product/apparel thumbnail — not design overlay art */
export const getStoreOrderSummaryProductImage = (item: ICartItemDetail): string =>
  item.primaryImage || item.frontImage || '/src/assets/svg/image_error.svg';

export const getStoreOrderSummaryProductTitle = (item: ICartItemDetail): string =>
  resolveStoreReviewProductTitle(item);

/** Combine same product (style) with different colors for the checkout summary bar */
export const groupStoreOrderSummaryProducts = (
  items: ICartItemDetail[]
): IStoreOrderSummaryProductGroup[] => {
  const productGroups = items.reduce((acc, item) => {
    const productKey = item.apparelStyleCode || `${item.id}`;

    if (!acc[productKey]) {
      acc[productKey] = {
        primaryItem: item,
        colors: [],
      };
    }

    const colorKey = String(item.designId || item.id);
    const existingColorKeys = new Set(
      acc[productKey].colors.map((colorInfo) => colorInfo.colorKey)
    );

    if (!existingColorKeys.has(colorKey)) {
      acc[productKey].colors.push({
        colorKey,
        hex: item.apparelHex || '#ccc',
        color: item.apparelColor || '',
      });
    }

    return acc;
  }, {} as Record<string, { primaryItem: ICartItemDetail; colors: Array<{ colorKey: string; hex: string; color: string }> }>);

  return Object.values(productGroups).map(({ primaryItem, colors }) => ({
    primaryItem,
    colors: colors.map(({ hex, color }) => ({ hex, color })),
  }));
};

export const formatStoreOrderSummaryCloseDate = (closeDate: string): string => {
  if (!closeDate.trim()) {
    return 'TBD';
  }

  const parsedCloseDate = parseLocalDateOnly(closeDate);

  if (!parsedCloseDate) {
    return 'TBD';
  }

  return formatAbbreviatedMonthDay(parsedCloseDate);
};

export const getStoreEstDeliveryDateRange = (closeDate: string): string => {
  if (!closeDate) {
    return 'TBD';
  }

  const parsedCloseDate = parseLocalDateOnly(closeDate);

  if (!parsedCloseDate) {
    return 'TBD';
  }

  const minDate = new Date(parsedCloseDate);
  const maxDate = new Date(parsedCloseDate);

  minDate.setDate(minDate.getDate() + STORE_ORDER_SUMMARY_EST_DELIVERY_MIN_DAYS_AFTER_CLOSE);
  maxDate.setDate(maxDate.getDate() + STORE_ORDER_SUMMARY_EST_DELIVERY_MAX_DAYS_AFTER_CLOSE);

  return formatAbbreviatedMonthDayRange(minDate, maxDate);
};
